import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the menu is visible */
  isOpen: boolean
  /** Max height before scrolling */
  maxHeight?: number
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectMenu component.
 *
 * Container for SelectHeader and SelectOption children. Only renders when isOpen is true.
 * Header is fixed at top while options scroll.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const SelectMenu = React.memo(
  ({
    isOpen,
    maxHeight = 240,
    children,
    style,
    className,
    ...props
  }: SelectMenuProps) => {
    const [css, theme] = useStyletron()

    if (!isOpen) return null

    const menuClassName = css(getMenuStyles(theme))
    const scrollContainerClassName = css(
      getScrollContainerStyles({ maxHeight })
    )

    // Separate header from options
    const childArray = React.Children.toArray(children)
    const header = childArray.find(
      child =>
        React.isValidElement(child) &&
        child.type &&
        (child.type as any).displayName === 'SelectHeader'
    )
    const options = childArray.filter(
      child =>
        !React.isValidElement(child) ||
        !child.type ||
        (child.type as any).displayName !== 'SelectHeader'
    )

    return (
      <div
        className={`${menuClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {header}
        <div className={scrollContainerClassName}>{options}</div>
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface ScrollContainerStyleParams {
  maxHeight: number
}

const getMenuStyles = (theme: any) => ({
  position: 'absolute' as const,
  top: '100%',
  left: '0',
  right: '0',
  marginTop: '1px',
  backgroundColor: theme.colors.white,
  borderWidth: '1px',
  borderStyle: 'solid' as const,
  borderColor: theme.colors.grey20,
  borderRadius: theme.borders.radius200,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 100,
  overflow: 'hidden' as const,
})

const getScrollContainerStyles = ({
  maxHeight,
}: ScrollContainerStyleParams) => ({
  overflowY: 'auto' as const,
  maxHeight: `${maxHeight}px`,
})

export default SelectMenu
